import React from 'react';

import {
  useRegisterSiteUserMutation,
  useSignUpActivityLazyQuery,
  useSignUpActivity_SaveSiteUserMutation,
  useVerifyEmailNotInUseLazyQuery
} from 'generated/graphql';
import {
  authService,
  useLoading,
  validationUtil
} from 'modules';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';



const __formSchema = yup.object({
  firstName: validationUtil.nonEmptyStringSchema.required('First name is required.'),
  lastName: validationUtil.nonEmptyStringSchema.required('Last name is required.'),
  email: validationUtil.emailSchema.required('Email is required.'),
  password: validationUtil.passwordSchema.required('Password is required.'),
  confirmPassword: validationUtil.confirmPasswordSchema('password').required('Confirm Password is required.')
}).required();

export type IFormData = yup.InferType<typeof __formSchema>;



export function useSignUpActivity() {
  const [getLoggedInUser, getLoggedInUserQueryTuple] = __useGetLoggedInUserQuery();
  const [verifyEmailNotInUse, verifyEmailNotInUseTuple] = __useVerifyEmailNotInUse();
  const [register, registerTuple] = __useRegisterMutation();
  const [saveSiteUser, saveSiteUserTuple] = __useSaveSiteUserMutation();
  const loading = useLoading([
    verifyEmailNotInUseTuple,
    registerTuple,
    saveSiteUserTuple,
    getLoggedInUserQueryTuple
  ]);

  const form = useForm<IFormData>({
    resolver: yupResolver(__formSchema)
  });

  return {
    form,
    loading,
    data: getLoggedInUserQueryTuple.data,
    queries: {
      verifyEmailNotInUse,
      getLoggedInUser
    },
    mutations: {
      register,
      saveSiteUser
    }
  };
}



export interface ILoggedInUser {
  id: string;
}

export interface IData {
  loggedInUser: ILoggedInUser | null;
}

function __useGetLoggedInUserQuery() {
  const [fnQuery, tuple] = useSignUpActivityLazyQuery({
    fetchPolicy: 'network-only'
  });

  const wrappedTuple = React.useMemo(() => {
    const data: IData = {
      loggedInUser: tuple.data?.loggedInUser ? {
        id: tuple.data.loggedInUser.id
      } : null
    };

    return {
      ...tuple,
      data
    };
  }, [tuple]);

  const wrappedCallback = React.useCallback(async () => {
    const tuple = await fnQuery({
      fetchPolicy: 'network-only'
    });
    console.log(tuple);

    const data: IData = {
      loggedInUser: tuple.data?.loggedInUser ? {
        id: tuple.data.loggedInUser.id
      } : null
    };

    return {
      ...tuple,
      data
    };
  },
  [fnQuery]);

  return [
    wrappedCallback,
    wrappedTuple
  ] as const;
}



function __useVerifyEmailNotInUse() {
  const [fnQuery, tuple] = useVerifyEmailNotInUseLazyQuery({
    fetchPolicy: 'network-only'
  });

  async function fnWrappedQuery(email: string) {
    const result = await fnQuery({ variables: { email } });

    return result.data?.verifyEmailNotInUse ?? false;
  }

  return [fnWrappedQuery, tuple] as const;
}



function __useRegisterMutation() {
  const [tuple, setTuple] = React.useState<{ loading: boolean; }>({
    loading: false
  });
  const [register, registerTuple] = useRegisterSiteUserMutation();



  async function fnRegisterCognitoUser(data: authService.ISignUpData) {
    try {
      setTuple({ loading: true });
      const uid = await authService.register(data);

      await register({
        variables: {
          input: {
            authProviderUserId: uid,
            email: data.email
          }
        }
      });

    } finally {
      setTuple({ loading: false });
    }
  }



  return [
    fnRegisterCognitoUser,
    {
      ...registerTuple,
      loading: registerTuple.loading || tuple.loading
    }
  ] as const;
}



function __useSaveSiteUserMutation() {
  const [fnMutate, tuple] = useSignUpActivity_SaveSiteUserMutation({});

  const wrappedMutate = React.useCallback(
    async ({
      userId, firstName, lastName
    }: {
      userId: string,
      firstName: string,
      lastName: string;
    }) => {
      await fnMutate({
        variables: {
          id: userId,
          firstName,
          lastName
        }
      });
    },
    [fnMutate]);

  return React.useMemo(() => [
    wrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const, [tuple, wrappedMutate]);
}
